import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Loader2, Lock, User as UserIcon } from 'lucide-react';

import CarteAuth, { ACCENT, BoutonPrincipal, Champ, Message } from '../components/CarteAuth';
import { adminApi } from '../services/api.jsx';

/* ═══════════════════════════════════════════════════════════════════════════
   CONNEXION AU BACK-OFFICE

   Le champ accepte l'identifiant ou l'adresse e-mail : l'identifiant est
   choisi une fois puis oublié, l'adresse est retenue parce qu'on s'en sert
   tous les jours. Refuser la seconde imposait une réinitialisation de mot de
   passe pour un simple trou de mémoire.

   La page partage sa coquille avec l'activation et la récupération de mot de
   passe. Les trois forment un même parcours et doivent se ressembler.
   ═══════════════════════════════════════════════════════════════════════════ */

export default function Login({ onLogin }) {
  // Motif transmis par la déconnexion automatique. Il remplace le
  // `window.alert` qui bloquait le fil d'exécution et s'affichait avant même
  // que l'utilisateur ait été redirigé.
  const location = useLocation();
  const motifDeconnexion = location.state?.motif || null;

  const [form, setForm] = useState({ username: '', password: '' });
  const [visible, setVisible] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);

  const soumettre = async (evenement) => {
    evenement.preventDefault();
    setEnvoi(true);
    setErreur(null);
    try {
      onLogin(await adminApi.login(form));
    } catch (e) {
      setErreur(e.message || "Accès refusé.");
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <CarteAuth
      surtitre="Espace administrateur"
      titre="Connexion"
      description="Portail de gestion de la plateforme ASSO-PRO."
    >
      <form onSubmit={soumettre} className="space-y-5">
        <div className="space-y-1.5">
          <label htmlFor="identifiant" className="text-[12.5px] font-medium text-[var(--text-main)]">
            Identifiant ou adresse e-mail
          </label>
          <Champ id="identifiant" icone={UserIcon} type="text" required autoFocus
                 autoComplete="username"
                 placeholder="admin_01 ou vous@impactlab-cilis.org"
                 value={form.username}
                 onChange={(e) => setForm({ ...form, username: e.target.value })} />
        </div>

        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-3">
            <label htmlFor="motdepasse" className="text-[12.5px] font-medium text-[var(--text-main)]">
              Mot de passe
            </label>
            <Link to="/mot-de-passe-oublie" className="text-[12.5px] font-medium"
                  style={{ color: ACCENT }}>
              Oublié ?
            </Link>
          </div>
          <div className="relative">
            <Champ id="motdepasse" icone={Lock} required
                   type={visible ? 'text' : 'password'}
                   autoComplete="current-password" placeholder="••••••••"
                   value={form.password}
                   onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <button type="button" onClick={() => setVisible((v) => !v)}
                    aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]
                               hover:text-[var(--text-main)] transition-colors">
              {visible ? <EyeOff className="w-[17px] h-[17px]" />
                       : <Eye className="w-[17px] h-[17px]" />}
            </button>
          </div>
        </div>

        {motifDeconnexion && !erreur && (
          <Message ton="info">{motifDeconnexion}</Message>
        )}
        <Message>{erreur}</Message>

        <BoutonPrincipal type="submit" disabled={envoi} aria-busy={envoi || undefined}>
          {envoi ? <><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />Connexion…</> : 'Se connecter'}
        </BoutonPrincipal>
      </form>
    </CarteAuth>
  );
}
